import React, { useState, useEffect } from 'react';
import { StyleSheet, ActivityIndicator, Image, StyleProp, ViewStyle, View } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import * as Linking from 'expo-linking';
import hidProvisioningApi from '_/services/api/hid-provisioning';
import { showMessage } from 'react-native-flash-message';
import Button from '../Button';
import Text from '../Text';
import { useTranslation } from 'react-i18next';
import themes from '_/constants/themes';
import { fontFamily } from '_/config/theme';
import { appConfig } from '_/config/app';
import * as AuthSession from 'expo-auth-session/providers/google';
import { useDispatch } from 'react-redux';
import { updateHostCardNumberRequest } from '_/store/modules/host/actions';
import { HostCardNumbersModel } from '@w3lcome/types';
import { ProvisioningIssuanceTokensModel } from '@w3lcome/types/dist/models/provisioning-issuance-tokens.model';
import { useActiveAppState } from '_/hooks/useActiveAppState';

interface GoogleBtnProps {
  disabled?: boolean;
  loading?: boolean;
  onPress: () => void;
  content: string;
  isApple?: boolean;
  styles?: StyleProp<ViewStyle>;
}

interface AddWalletProps {
  issuanceToken: string;
  hostCardNumberId?: string;
  styles?: StyleProp<ViewStyle>;
}

export const WalletButon = ({
  disabled,
  loading,
  onPress,
  content,
  isApple,
  ...props
}: GoogleBtnProps) => {
  const { t: translate } = useTranslation();

  return (
    <Button
      disabled={disabled}
      onPress={() => onPress()}
      buttonStyles={[styles.btnWallet, loading && styles.btnLoading, props.styles]}
      outline
    >
      <Image
        resizeMode="contain"
        source={themes.images[isApple ? 'appleWalletIcon' : 'googleWalletIcon']}
        style={[{ width: 24, height: 24 }, loading && styles.textLoading]}
      />
      <Text style={[styles.btnText, styles.btnWalletText, loading && styles.textLoading]}>
        {translate(content)}
      </Text>
      {loading && <ActivityIndicator size="large" style={{ position: 'absolute' }} />}
    </Button>
  );
};

// Ensures that the browser is properly dismissed when the user comes back to the app
WebBrowser.maybeCompleteAuthSession();

const AddGoogleWalletHIDButton = ({
  hostCard,
  ...props
}: {
  hostCard: HostCardNumbersModel;
  styles?: StyleProp<ViewStyle>;
}) => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();

  const { id, issuanceToken, provisioningIssuanceToken } = hostCard;
  const clientId = appConfig.googleLoginConfig.googleClientId,
    androidClientId = appConfig.googleLoginConfig.androidClientId,
    iosClientId = appConfig.googleLoginConfig.iosClientId;

  const [loading, setLoading] = useState(false);

  const [request, response, promptAsync] = AuthSession.useIdTokenAuthRequest({
    clientId,
    androidClientId,
    iosClientId,
  });

  useEffect(() => {
    if (response) {
      if (response.type === 'success') {
        const { id_token } = response.params;
        callProvisioning(id_token);
      } else {
        setLoading(false);
        showMessage({
          message: translate('provisioning.googleAuthFailed'),
          type: 'danger',
        });
      }
    }
  }, [response]);

  const callProvisioning = async (authCode: string) => {
    setLoading(true);

    try {
      const payload = {
        issuanceToken,
        googleIdToken: authCode,
        hostCardNumberId: id,
      };

      if (!issuanceToken || !authCode) {
        throw new Error('Issuance token and googleIdToken is required.');
      }

      const provisioningUrl = await hidProvisioningApi.create(payload);

      if (provisioningUrl) {
        dispatch(
          updateHostCardNumberRequest({
            id,
            provisioningIssuanceToken: {
              ...provisioningIssuanceToken,
              issuanceToken,
              provisionedAt: new Date(),
            } as ProvisioningIssuanceTokensModel,
          })
        );

        await Linking.openURL(provisioningUrl);
      } else {
        throw new Error('Incomplete response.');
      }
    } catch (err) {
      showMessage({
        message: translate('provisioning.errorAddingToWallet'),
        type: 'danger',
      });
    }

    setLoading(false);
  };

  return (
    <WalletButon
      content={translate('provisioning.addToGoogleWallet')}
      disabled={!request || loading}
      loading={loading}
      onPress={promptAsync}
      styles={props.styles}
    />
  );
};

const AddAppleWalletHIDButton = ({ issuanceToken, hostCardNumberId, ...props }: AddWalletProps) => {
  const { t: translate } = useTranslation();
  const BASE_DOMAIN = `${appConfig.preCheckinURL}/addwallet`;
  const [isLoading, setIsLoading] = useState(false);

  const bridgeUrl = `${BASE_DOMAIN}?issuanceToken=${issuanceToken}&hostCardNumberId=${
    hostCardNumberId || ''
  }&isAppleMobile=true&showApple=true`;

  const handleAddToWallet = async () => {
    try {
      setIsLoading(true);

      await Linking.openURL(bridgeUrl);
    } catch (error) {
      console.error('Error initing Apple Wallet: ', error);
      showMessage({
        message: translate('provisioning.errorAddingToWallet'),
        type: 'danger',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={{ flex: 1 }}>
      <WalletButon
        content={translate('provisioning.addToAppleWallet')}
        disabled={isLoading}
        loading={isLoading}
        onPress={handleAddToWallet}
        styles={props.styles}
        isApple
      />
    </View>
  );
};

const styles = StyleSheet.create({
  btnWallet: {
    backgroundColor: themes.colors.darkTertiary,
    borderColor: themes.colors.lightSecondary,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingBlock: 4,
  },
  btnText: {
    color: themes.colors.lightPrimary,
    flex: 1,
    fontFamily: fontFamily.medium,
    fontSize: 18,
    textAlign: 'center',
  },
  btnWalletText: {
    color: themes.colors.lightPrimary,
  },
  textLoading: {
    opacity: 0.7,
  },
  btnLoading: {
    backgroundColor: themes.colors.darkQuaternary,
  },
});

export { AddGoogleWalletHIDButton, AddAppleWalletHIDButton };
